import {JSXElementConstructor, ReactElement} from "react";
import {FormFields} from "../../../../../../models/forms/form-field.interface";
import {PsoAgreementModelBase} from "../base/pso-agreement.model";
import {
    DegreeOfKinshipTypeEnum,
    IPsoFamilyAgreementFormFields,
    IPsoFamilyAgreementViewModel, isInFirst3Degrees,
    SpecificDegreeOfKinshipTypeEnum
} from "./pso-family-agreement.view-model.interface";
import {IPsoAgreementCommonPersistedFields} from "../base/pso-agreement.view-mode.interface";
import {NullableString} from "../../../../../../types/nullable-types";
import {MaturePassengerSessionStorageKeys} from "../../../passenger/mature-passenger-session-storage-keys";
import {
    PsoFamilyAgreementComponent
} from "../../../../../../components/territorial-continuity-regime/agreements/families/pso-family-agreement.component";
import {IPsoFamilyAgreementRequest} from "../../../../../airline-webapi/requests/pso-family-agreement.request";
import {PsoFamilySpecificDegreeTypeModel} from "./options/pso-family-specific-degree-type.model";
import {MaturePassengerModel} from "../../../passenger/mature-passenger.model";
import {Check} from "../../../../../../types/type-checking";


export class PsoFamilyAgreementModel extends PsoAgreementModelBase<IPsoFamilyAgreementFormFields> implements IPsoFamilyAgreementViewModel {
    constructor(passenger: MaturePassengerModel) {
        super(passenger);
        this.specificDegreeOfKinshipOptions = [
            new PsoFamilySpecificDegreeTypeModel(SpecificDegreeOfKinshipTypeEnum.Spouse, passenger.services),
            new PsoFamilySpecificDegreeTypeModel(SpecificDegreeOfKinshipTypeEnum.Child, passenger.services),
            new PsoFamilySpecificDegreeTypeModel(SpecificDegreeOfKinshipTypeEnum.Parent, passenger.services),
        ];
    }


    protected isDeclarationDownloaded(): boolean {
        return this.passenger.booking.pso.familyAgreements.downloaded;
    }


    protected _resetConfirmation(): void {
        this.passenger.booking.pso.familyAgreements.confirmed = false;
    }

    readonly specificDegreeOfKinshipOptions: PsoFamilySpecificDegreeTypeModel[];

    protected _createFields(): FormFields<IPsoFamilyAgreementFormFields> {
        const persistedFields = this._getPersistedFields()
        const language = this.services.language;
        return {
            ...this._createCommonFields(persistedFields),
            degreeOfKinshipType: this._createField<DegreeOfKinshipTypeEnum>({
                fieldName: () => language.translate('Please indicate the applicable degree of kinship with a Sardinian Resident:'),
                initialValue: () => persistedFields?.degreeOfKinshipType
            }),
            degreeOfKinshipDescription: this._createField<string>({
                fieldName: () => language.translate('Please indicate the relationship between the Sardinian Resident and the beneficiary of the fare:'),
                initialValue: () => persistedFields?.degreeOfKinshipDescription,
                maxLength: 100,
                isHidden: () => !isInFirst3Degrees(this.fields.degreeOfKinshipType.value),
                isRequired: () => !this.fields.degreeOfKinshipDescription.isHidden,
            }),
            guardianshipIssuedByCourt: this._createField<string>({
                fieldName: () => language.translate('Issued by the Court of'),
                initialValue: () => persistedFields?.guardianshipIssuedByCourt,
                maxLength: 100,
                isHidden: () => this.fields.degreeOfKinshipType.value !== DegreeOfKinshipTypeEnum.LegalGuardian,
                isRequired: () => !this.fields.guardianshipIssuedByCourt.isHidden,
            }),
            guardianshipIssueDate: this._createField<Date>({
                fieldName: () => language.translate('Date of issuance'),
                initialValue: () => this.services.time.tryConvertToDate(persistedFields?.guardianshipIssueDate),
                isHidden: () => this.fields.guardianshipIssuedByCourt.isHidden,
                isRequired: () => !this.fields.guardianshipIssuedByCourt.isHidden,
            }),
            guardianshipDocumentNumber: this._createField<string>({
                fieldName: () => language.translate('Documen  no.'),
                initialValue: () => persistedFields?.guardianshipDocumentNumber,
                isHidden: () => this.fields.guardianshipIssuedByCourt.isHidden,
                isRequired: () => !this.fields.guardianshipIssuedByCourt.isHidden,
                maxLength: 30,
            }),
            specificDegreeOfKinshipType: this._createField<SpecificDegreeOfKinshipTypeEnum>({
                fieldName: () => language.translate('Please indicate the applicable degree of kinship with a parental of a Sardinian Resident:'),
                initialValue: () => persistedFields?.specificDegreeOfKinshipType,
                isRequired: () => false,
                isHidden: () => !isInFirst3Degrees(this.fields.degreeOfKinshipType.value),
                maxLength: 100
            }),
            specificDegreeOfKinshipFullName: this._createField<string>({
                fieldName: () => language.translate('Full name (first and last name) of the relative.'),
                initialValue: () => persistedFields?.specificDegreeOfKinshipFullName,
                maxLength: 100,
                isHidden: () => this.fields.specificDegreeOfKinshipType.isHidden || Check.isNullOrUndefined(this.fields.specificDegreeOfKinshipType.value),
                isRequired: () => !this.fields.specificDegreeOfKinshipFullName.isHidden,
            }),
            residentFullName: this._createField<string>({
                fieldName: () => language.translate('Full name'),
                initialValue: () => persistedFields?.residentFullName,
                maxLength: 100
            }),
            residentMunicipality: this._createField<string>({
                fieldName: () => language.translate('Municipality of residence'),
                initialValue: () => persistedFields?.residentMunicipality,
                maxLength: 100
            }),
            residentAddress: this._createField<string>({
                fieldName: () => language.translate('Residential address'),
                initialValue: () => persistedFields?.residentAddress,
                maxLength: 100
            }),
            residentEmail: this._createEmailField({
                fieldName: () => language.translate('E-mail of the resident relative in Sardinia'),
                initialValue: () => persistedFields?.residentEmail,
            }),
            residentPhone: this._createPhoneField({
                fieldName: () => language.translate('Phone number of the resident relative in Sardinia'),
                initialValue: () => persistedFields?.residentPhone,
                maxLength: 20
            }),
            residentPhoneCountryCode: this._createField<string>({
                fieldName: () => language.translate('Country'),
                isHidden: () => true,
                isRequired: false,
                defaultValue: this.services.configuration.defaultCountryCode,
                initialValue: () => this.services.configuration.defaultCountryCode
            }),
        }
    }

    private getSelectedSpecificDegreeOfKinshipType(): PsoFamilySpecificDegreeTypeModel | null {

        return this.specificDegreeOfKinshipOptions.find(o => o.value === this.fields.specificDegreeOfKinshipType.value) ?? null;

    }

    protected _persistFields(): void {
        const persistedFields: IPsoFamilyAgreementPersistedFields = {
            ...this._persistCommonFields(),
            degreeOfKinshipType: this.fields.degreeOfKinshipType.value,
            degreeOfKinshipDescription: this.fields.degreeOfKinshipDescription.value,
            specificDegreeOfKinshipType: this.fields.specificDegreeOfKinshipType.value,
            specificDegreeOfKinshipFullName: this.fields.specificDegreeOfKinshipFullName.value,
            guardianshipIssueDate:  this.fields.guardianshipIssueDate.value && this.services.time.formatYYYY_MM_DD(this.fields.guardianshipIssueDate.value),
            guardianshipDocumentNumber: this.fields.guardianshipDocumentNumber.value,
            guardianshipIssuedByCourt: this.fields.guardianshipIssuedByCourt.value,
            residentEmail: this.fields.residentEmail.value,
            residentPhone: this.fields.residentPhone.value,
            residentPhoneCountryCode: this.fields.residentPhoneCountryCode.value,
            residentAddress: this.fields.residentAddress.value,
            residentFullName: this.fields.residentFullName.value,
            residentMunicipality: this.fields.residentMunicipality.value,
        }
        this.passenger.storage.setJson(MaturePassengerSessionStorageKeys.psoFamilyAgreement, persistedFields);
    }


    private _getPersistedFields(): IPsoFamilyAgreementPersistedFields | null {
        return this.passenger.storage.getJson<IPsoFamilyAgreementPersistedFields>(MaturePassengerSessionStorageKeys.psoFamilyAgreement) ?? null;
    }

    buildApiRequest(): IPsoFamilyAgreementRequest {
        return {
            versionNumber: 2,
            declarationDate: this.declarationDate.toISOString(),
            psoMarketName: this.passenger.booking.pso.psoMarket?.marketName ?? "",

            firstName: this.passengerFirstName,
            lastName: this.passengerLastName,
            dateOfBirth: this.services.time.formatYYYY_MM_DD(this.fields.dateOfBirth.value),
            passengerBirthLocation: this.fields.birthLocation.value!,
            passengerResidenceLocation: this.fields.residenceLocation.value!,
            passengerFiscalCode: this.fields.fiscalCode.value!,
            passengerPhoneNumber: this.fields.phoneNumber.value!,
            passengerEmail: this.fields.email.value!,
            degreeOfKinshipType: this.fields.degreeOfKinshipType.value!,
            ...this._buildApiRequestDegreeOfKinshipDescriptionFields(),
            ...this._buildApiRequestSpecificDegreeOfKinshipFields(),
            ...this._buildApiRequestLegalGuardianFields(),
            residentFullName: this.fields.residentFullName.value!,
            residentMunicipality: this.fields.residentMunicipality.value!,
            residentAddress: this.fields.residentAddress.value!,
            residentEmail: this.fields.residentEmail.value!,
            residentPhone: this.fields.residentPhone.value!
        }
    }

    private _buildApiRequestDegreeOfKinshipDescriptionFields() {
        if(isInFirst3Degrees(this.fields.degreeOfKinshipType.value)) {
            return {
                degreeOfKinshipDescription: this.fields.degreeOfKinshipDescription.value!,
            }
        }
        return {
            degreeOfKinshipDescription: null,
        };
    }

    private _buildApiRequestSpecificDegreeOfKinshipFields() {
        if(!isInFirst3Degrees(this.fields.degreeOfKinshipType.value)) {
            return {
                specificDegreeOfKinshipType: null,
                specificDegreeOfKinshipFullName: null,
            }
        }

        if(Check.isNullOrUndefined(this.fields.specificDegreeOfKinshipType.value)) {
            return {
                specificDegreeOfKinshipType: null,
                specificDegreeOfKinshipFullName: null,
            }
        }
        return {
            specificDegreeOfKinshipType: this.fields.specificDegreeOfKinshipType.value,
            specificDegreeOfKinshipFullName: this.fields.specificDegreeOfKinshipFullName.value,
        };
    }

    private _buildApiRequestLegalGuardianFields() {
        if(this.fields.degreeOfKinshipType.value !== DegreeOfKinshipTypeEnum.LegalGuardian) {
            return {
                guardianshipIssuedByCourt: null,
                guardianshipIssueDate: null,
                guardianshipDocumentNumber: null
            };
        }
        return {
            guardianshipIssuedByCourt: this.fields.guardianshipIssuedByCourt.value,
            guardianshipIssueDate: this.fields.guardianshipIssueDate.value && this.services.time.formatYYYY_MM_DD(this.fields.guardianshipIssueDate.value),
            guardianshipDocumentNumber: this.fields.guardianshipDocumentNumber.value,
        }
    }

    renderComponent(): ReactElement<any, string | JSXElementConstructor<any>> {
        return (
            <PsoFamilyAgreementComponent familyAgreement={this}/>
        );
    }

}

interface IPsoFamilyAgreementPersistedFields extends  IPsoAgreementCommonPersistedFields {
    degreeOfKinshipType: DegreeOfKinshipTypeEnum | null;
    degreeOfKinshipDescription: NullableString;
    guardianshipIssuedByCourt: NullableString;
    guardianshipIssueDate: NullableString;
    guardianshipDocumentNumber: NullableString;
    specificDegreeOfKinshipType: SpecificDegreeOfKinshipTypeEnum | null;
    specificDegreeOfKinshipFullName: NullableString;
    residentFullName: NullableString;
    residentMunicipality: NullableString;
    residentAddress: NullableString;
    residentEmail: NullableString;
    residentPhone: NullableString;
    residentPhoneCountryCode: NullableString;
}